/**
 * Utilidad para manejar errores de GraphQL de forma consistente
 */

/**
 * Extrae el mensaje de error de una respuesta GraphQL
 * @param {Object} response - Respuesta de axios
 * @param {string} mutationName - Nombre de la mutación (opcional, para acceso directo)
 * @returns {string|null} - Mensaje de error o null si no hay error
 */
export const getGraphQLError = (response, mutationName = null) => {
  // Verificar si hay errores en response.data.errors (errores de GraphQL)
  if (response?.data?.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
    const error = response.data.errors[0];
    return error.message || 'Error en la petición';
  }

  // Si hay un mutationName, intentar obtener el error de la mutación
  if (mutationName && response?.data?.data?.[mutationName]) {
    const mutationResult = response.data.data[mutationName];
    if (mutationResult?.error) {
      return mutationResult.error;
    }
    // Si success es false pero no hay error explícito
    if (mutationResult?.success === false && !mutationResult?.error) {
      return 'La operación no se pudo completar';
    }
  }

  return null;
};

/**
 * Verifica si una respuesta GraphQL tiene errores
 * @param {Object} response - Respuesta de axios
 * @param {string} mutationName - Nombre de la mutación (opcional)
 * @returns {boolean}
 */
export const hasGraphQLError = (response, mutationName = null) => {
  // Verificar errores de GraphQL
  if (response?.data?.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
    return true;
  }

  // Verificar error en la mutación
  if (mutationName && response?.data?.data?.[mutationName]) {
    const mutationResult = response.data.data[mutationName];
    if (mutationResult?.error || mutationResult?.success === false) {
      return true;
    }
  }

  return false;
};

/**
 * Obtiene el resultado de una mutación GraphQL de forma segura
 * @param {Object} response - Respuesta de axios
 * @param {string} mutationName - Nombre de la mutación
 * @returns {Object|null} - Resultado de la mutación o null si hay error
 */
export const getGraphQLResult = (response, mutationName) => {
  if (!mutationName || !response?.data?.data?.[mutationName]) {
    return null;
  }

  const mutationResult = response.data.data[mutationName];
  
  // Si hay error de GraphQL, retornar null
  if (response?.data?.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
    return null;
  }

  // Si la mutación tiene error o success es false, retornar null
  if (mutationResult?.error || mutationResult?.success === false) {
    return null;
  }

  return mutationResult;
};

/**
 * Maneja errores de permisos de forma especial
 * @param {string} errorMessage - Mensaje de error
 * @returns {string} - Mensaje formateado
 */
export const formatPermissionError = (errorMessage) => {
  if (!errorMessage) return 'Error desconocido';
  
  // Detectar errores de permisos - solo devolver el mensaje simple
  if (errorMessage.includes('No tiene permisos') || errorMessage.includes('permisos para realizar')) {
    return errorMessage; // Devolver solo el mensaje sin formato adicional
  }
  
  // Detectar errores de autenticación
  if (errorMessage.includes('no autenticado') || errorMessage.includes('autenticado')) {
    return 'Su sesión ha expirado. Por favor, inicie sesión nuevamente.';
  }
  
  return errorMessage;
};

